import { clsx, type ClassValue } from 'clsx'
import { useEffect, useState, useSyncExternalStore } from 'react'

export function cn(...parts: ClassValue[]): string {
  return clsx(parts)
}

/** A CSS media query as React state (e.g. '(min-width: 48rem)'). */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (notify) => {
      const mq = window.matchMedia(query)
      mq.addEventListener('change', notify)
      return () => mq.removeEventListener('change', notify)
    },
    () => window.matchMedia(query).matches,
    () => false,
  )
}

/** True on phone widths (below the md breakpoint, 48rem). */
export function usePhone(): boolean {
  return !useMediaQuery('(min-width: 48rem)')
}

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

/** Skeleton delay from tokens.css (300 ms). */
export function skeletonDelay(): number {
  return parseFloat(cssVar('--skeleton-delay')) || 300
}

/** True once `ms` have passed since mount; used so skeletons appear only after 300 ms. */
export function useDelayed(ms: number): boolean {
  const [ready, setReady] = useState(ms <= 0)
  useEffect(() => {
    if (ms <= 0) return
    const t = window.setTimeout(() => setReady(true), ms)
    return () => window.clearTimeout(t)
  }, [ms])
  return ready
}

/** A motion duration token in seconds for Motion (0 under reduced motion). */
export function duration(step: 'instant' | 'fast' | 'base' | 'slow'): number {
  return (parseFloat(cssVar(`--duration-${step}`)) || 0) / 1000
}

let probe: CanvasRenderingContext2D | null = null

/**
 * Resolves a colour token (e.g. '--color-register') for canvas and WebGL code such as sigma,
 * which cannot read CSS variables. Returns '#rrggbb' for the theme that is active now.
 */
export function tokenColor(name: string): string {
  probe ??= document.createElement('canvas').getContext('2d', { willReadFrequently: true })
  if (!probe) return ''
  probe.clearRect(0, 0, 1, 1)
  probe.fillStyle = cssVar(name)
  probe.fillRect(0, 0, 1, 1)
  const [r = 0, g = 0, b = 0] = probe.getImageData(0, 0, 1, 1).data
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')
}

/** Initials in the masked style used for register persons: 'KV' -> 'K. V.' */
export function maskedInitials(initials: string): string {
  return initials
    .split('')
    .filter((c) => c.trim())
    .map((c) => `${c.toUpperCase()}.`)
    .join(' ')
}
